import { useEffect, useMemo, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { AssistanceServiceImpl } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useAISmartButtonContext } from '../../context';
import { isBrowser } from '../../utils/is-browser';
import { schedulePostPaint } from '../../utils/schedule-post-paint';

const LOOK_BACK_PERIOD_MS = 36 * 60 * 60 * 1000;
const CACHE_TTL_MS = 5 * 60 * 1000;
const IN_PROGRESS_POLL_BASE_INTERVAL_MS = 60 * 1000;
const IN_PROGRESS_POLL_BACKOFF_STEP_MS = 30 * 1000;
const IN_PROGRESS_POLL_ATTEMPTS_PER_STEP = 5;
const IN_PROGRESS_POLL_MAX_INTERVAL_MS = CACHE_TTL_MS;
const CACHE_KEY = 'rovo-button-unread-status-v1';

type UnreadConversationSummary =
	| {
			hasUnread: true;
			inProgressCount: number;
			isStatusReady: boolean;
			mostRecentInProgress: { completedAt: string | null; id: string; name: string | null } | null;
			mostRecentUnread: {
				completedAt: string | null;
				id: string;
				name: string | null;
			};
			unreadCount: number;
	  }
	| {
			hasUnread: false;
			inProgressCount: number;
			isStatusReady: boolean;
			mostRecentInProgress: { completedAt: string | null; id: string; name: string | null } | null;
			mostRecentUnread: null;
			unreadCount: 0;
	  };

type CachedResult = {
	cachedAt: number;
	hasUnread: boolean;
	inProgressCount: number;
	mostRecentInProgress: { completedAt: string | null; id: string; name: string | null } | null;
	mostRecentUnread: { completedAt: string | null; id: string; name: string | null } | null;
	stale?: boolean;
	unreadCount: number;
};

function readCache(): { data: UnreadConversationSummary; isStale: boolean } | null {
	if (!isBrowser()) {
		return null;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return null;
		}
		const parsed: CachedResult = JSON.parse(raw);
		// Entries older than TTL are fully expired — discard them
		if (Date.now() - parsed.cachedAt >= CACHE_TTL_MS) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			sessionStorage.removeItem(CACHE_KEY);
			return null;
		}
		const { cachedAt: _cachedAt, stale, ...result } = parsed;
		// Migrate cache entries written before unreadCount / inProgressCount / mostRecentInProgress were added
		const data: UnreadConversationSummary = {
			...result,
			unreadCount: result.unreadCount ?? (result.hasUnread ? 1 : 0),
			inProgressCount: result.inProgressCount ?? 0,
			mostRecentInProgress: result.mostRecentInProgress ?? null,
			isStatusReady: stale !== true,
		} as UnreadConversationSummary;
		return { data, isStale: stale === true };
	} catch {
		return null;
	}
}

function writeCache(result: UnreadConversationSummary): void {
	if (!isBrowser()) {
		return;
	}
	try {
		const { isStatusReady: _isStatusReady, ...cacheableResult } = result;
		const toStore: CachedResult = { ...cacheableResult, cachedAt: Date.now() };
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		sessionStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
	} catch {
		// sessionStorage may be unavailable (private browsing quota, etc.) — fail silently
	}
}

/**
 * Marks the cached unread conversation status as stale without removing it.
 * The cached value continues to be displayed while a background re-fetch
 * updates the indicator — stale-while-revalidate.
 *
 * Call this directly when the user closes the mini-modal or sidebar. After marking
 * stale, the next call to `fetchAndUpdate` (triggered by post-paint scheduling or
 * window focus) will show the cached value immediately while re-fetching in the
 * background, then update the indicator once the fresh result arrives.
 */
export function markUnreadCacheStale(): void {
	if (!isBrowser()) {
		return;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return;
		}
		const parsed: CachedResult = JSON.parse(raw);
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...parsed, stale: true }));
	} catch {
		// sessionStorage may be unavailable — fail silently
	}
}

const NO_UNREAD: UnreadConversationSummary = {
	hasUnread: false,
	unreadCount: 0,
	inProgressCount: 0,
	mostRecentInProgress: null,
	mostRecentUnread: null,
	isStatusReady: false,
};

/**
 * Fetches conversation channel data after the page has painted (post-vc90) and
 * derives whether the user has any unread conversations from the past 36 hours.
 *
 * Results are cached in sessionStorage with a 5-minute TTL and a stale-while-revalidate
 * strategy: when the cache is marked stale (e.g. after the user closes the mini-modal or
 * sidebar), the cached value is shown immediately while a background re-fetch updates the
 * indicator without causing a flash.
 *
 * On any fetch failure, the currently displayed status is preserved rather than resetting
 * to no-indicator — a false negative is worse UX than a briefly stale indicator.
 */
export function useUnreadConversationStatus({
	enabled,
	mode,
}: {
	enabled: boolean;
	mode?: string;
}): UnreadConversationSummary {
	const initialCacheRef = useRef(enabled ? readCache() : null);
	const [status, setStatus] = useState<UnreadConversationSummary>(() =>
		enabled ? (initialCacheRef.current?.data ?? NO_UNREAD) : NO_UNREAD,
	);
	const { conversationAssistantConfig, product } = useAISmartButtonContext();
	const baseUrl = conversationAssistantConfig?.assistanceServiceConfig?.baseUrl;
	const headers = conversationAssistantConfig?.assistanceServiceConfig?.headers;
	const headersKey = headers ? JSON.stringify(headers) : '';
	const assistanceService = useMemo(
		() =>
			new AssistanceServiceImpl({
				product,
				experienceId: 'ai-mate',
				baseUrl,
				headers,
			}),
		// headersKey is the stable proxy for the headers object (which gets a new
		// identity every render from context); keying on it prevents assistanceService
		// from being recreated on every render, which otherwise re-runs the fetch
		// effect on each hover-induced re-render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[product, baseUrl, headersKey],
	);

	const fetchAndUpdateRef = useRef<(options?: { forceNetwork?: boolean }) => Promise<void>>(
		async () => {},
	);
	const isFetchingRef = useRef(false);
	const prevModeRef = useRef<string | undefined>(mode);

	useEffect(() => {
		if (!enabled) {
			// Reset the in-flight guard so the next enable can fetch immediately.
			// Without this, a fetch that was in-flight when enabled flipped to false
			// would leave isFetchingRef stuck as true, blocking the next fetch.
			isFetchingRef.current = false;
			setStatus(NO_UNREAD);
			return;
		}

		let cancelled = false;

		const fetchAndUpdate = async (options?: { forceNetwork?: boolean }) => {
			const forceNetwork = options?.forceNetwork ?? false;
			if (isFetchingRef.current) {
				return;
			}
			isFetchingRef.current = true;
			try {
				if (!forceNetwork) {
					const cacheResult = readCache();
					if (cacheResult) {
						if (!cancelled) {
							setStatus((prev) => {
								if (
									prev.hasUnread === cacheResult.data.hasUnread &&
									prev.unreadCount === cacheResult.data.unreadCount &&
									prev.inProgressCount === cacheResult.data.inProgressCount &&
									prev.mostRecentInProgress?.id === cacheResult.data.mostRecentInProgress?.id &&
									prev.mostRecentUnread?.id === cacheResult.data.mostRecentUnread?.id &&
									prev.isStatusReady === cacheResult.data.isStatusReady
								) {
									return prev;
								}
								return cacheResult.data;
							});
						}
						if (!cacheResult.isStale) {
							return;
						}
						// stale — fall through to background re-fetch
					}
				}

				const { items: channels } = await assistanceService.getConversationChannels();
				if (cancelled) {
					return;
				}

				const recentInProgress = channels
					.filter((channel) => channel.last_agent_message_status === 'IN_PROGRESS')
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});
				const inProgressCount = recentInProgress.length;
				const mostRecentInProgress = recentInProgress[0]
					? {
							id: String(recentInProgress[0].id),
							name: recentInProgress[0].name ?? null,
							completedAt: recentInProgress[0].last_message_timestamp ?? null,
						}
					: null;

				const now = Date.now();
				const recentUnread = channels
					.filter((channel) => {
						// readStatus === false is the explicit unread signal; null means a legacy/old
						// conversation with no read-tracking — exclude it to avoid false positives.
						const isUnread = channel.readStatus === false;
						// Only show indicator for completed agent responses — exclude IN_PROGRESS (streaming),
						// FAILED, CANCELLED, and legacy channels without this field.
						const hasSuccessfulAgentMessage = channel.last_agent_message_status === 'SUCCESSFUL';
						const timestamp = channel.last_message_timestamp;
						const isRecent =
							!!timestamp && now - new Date(timestamp).getTime() < LOOK_BACK_PERIOD_MS;
						return isUnread && hasSuccessfulAgentMessage && isRecent;
					})
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});

				const result: UnreadConversationSummary =
					recentUnread.length > 0
						? {
								hasUnread: true,
								unreadCount: recentUnread.length,
								inProgressCount,
								mostRecentInProgress,
								mostRecentUnread: {
									id: String(recentUnread[0].id),
									name: recentUnread[0].name ?? null,
									completedAt: recentUnread[0].last_message_timestamp ?? null,
								},
								isStatusReady: true,
							}
						: {
								hasUnread: false,
								unreadCount: 0,
								inProgressCount,
								mostRecentInProgress,
								mostRecentUnread: null,
								isStatusReady: true,
							};

				writeCache(result);
				setStatus(result);
			} catch {
				// Fail silently — keep the current displayed status rather than forcing NO_UNREAD.
				// If stale data was being shown, it continues to show — better UX than a false negative.
			} finally {
				isFetchingRef.current = false;
			}
		};

		fetchAndUpdateRef.current = fetchAndUpdate;

		// Defer the initial fetch until after paint/idle (post-vc90 safe)
		const cancelPostPaint = schedulePostPaint(() => {
			void fetchAndUpdate();
		});

		// Refresh on window focus once the cache has expired
		const unbindFocus = isBrowser()
			? bindAll(window, [
					{
						type: 'focus',
						listener: () => {
							void fetchAndUpdate();
						},
					},
				])
			: null;

		return () => {
			cancelled = true;
			cancelPostPaint();
			unbindFocus?.();
		};
	}, [assistanceService, enabled]);

	useEffect(() => {
		if (!enabled) {
			prevModeRef.current = mode;
			return;
		}
		if (mode === undefined && prevModeRef.current !== undefined) {
			// User closed the sidebar — mark cache stale and re-fetch in the background
			markUnreadCacheStale();
			void fetchAndUpdateRef.current();
		}
		prevModeRef.current = mode;
	}, [mode, enabled]);

	const hasInProgress = status.inProgressCount > 0;

	useEffect(() => {
		// Killswitch is read once per polling lifecycle (intentionally not an effect dep): toggling it
		// affects future in-progress polling cycles, not one that is already active. Exposure is
		// suppressed because this is an operational killswitch, not a measured experiment.
		const isPollingDisabled = FeatureGates.getExperimentValue<boolean>(
			'rovo_button_in_progress_polling_killswitch',
			'value',
			false,
			{ fireExperimentExposure: false },
		);
		if (isPollingDisabled || !enabled || !hasInProgress || !isBrowser()) {
			return;
		}

		const refetch = () => {
			// Force a real network fetch, bypassing the cache-first short-circuit.
			void fetchAndUpdateRef.current({ forceNetwork: true });
		};

		// Poll while a message is in progress, backing off over time: start at the base
		// interval and add a step every N attempts, until the interval reaches the cache
		// window — at which point polling stops (the next focus/mount/sidebar-close refreshes).
		let attemptsAtCurrentInterval = 0;
		let currentIntervalMs = IN_PROGRESS_POLL_BASE_INTERVAL_MS;
		let timeoutId: ReturnType<typeof setTimeout>;

		const scheduleNext = () => {
			timeoutId = setTimeout(onTick, currentIntervalMs);
		};

		function onTick() {
			// Skip ticks while the tab is backgrounded: do not fetch and do not advance the
			// backoff; simply re-schedule at the same interval.
			if (document.visibilityState !== 'visible') {
				scheduleNext();
				return;
			}

			refetch();
			attemptsAtCurrentInterval += 1;
			if (attemptsAtCurrentInterval >= IN_PROGRESS_POLL_ATTEMPTS_PER_STEP) {
				attemptsAtCurrentInterval = 0;
				currentIntervalMs += IN_PROGRESS_POLL_BACKOFF_STEP_MS;
			}

			// Once the interval reaches the cache window, stop polling entirely.
			if (currentIntervalMs >= IN_PROGRESS_POLL_MAX_INTERVAL_MS) {
				return;
			}

			scheduleNext();
		}

		scheduleNext();

		const unbindVisibility = bindAll(document, [
			{
				type: 'visibilitychange',
				listener: () => {
					// Catch-up refetch when the tab becomes visible again. This does not count
					// toward the backoff and does not alter the pending schedule.
					if (document.visibilityState === 'visible') {
						refetch();
					}
				},
			},
		]);

		return () => {
			clearTimeout(timeoutId);
			unbindVisibility();
		};
	}, [enabled, hasInProgress]);

	return status;
}
